/**
 * RTWO Cryptography & Identity Engine — Signature Creation Service
 */

import { CRYPTO_SERVICE_IDS } from '../domain-models';
import type { RTWOSignature, SignatureId, SignatureStatus } from '../domain-models';
import type {
  ISignatureCreationService,
  SignatureCreationRequest,
  SignatureCreationResult,
} from '../service-interfaces';
import type { CryptoRepositories } from '../repositories/crypto-repositories';
import type { KeyProviderRegistry } from '../registry/provider-registries';
import { CryptoEventTypes } from '../events/crypto-event-bus';
import { BaseCryptoService, type CryptoServiceContext } from './base-crypto-service';

export class SignatureCreationService
  extends BaseCryptoService
  implements ISignatureCreationService
{
  readonly serviceId = CRYPTO_SERVICE_IDS.SIGNATURE_CREATION;
  readonly serviceName = 'Signature Creation Service';
  readonly description =
    'Creates cryptographic signatures via Key Provider. Never accesses private keys directly.';
  readonly layer = 'R6' as const;
  readonly integrations = [
    'R4 Blockchain Protocol Engine',
    'R6 Security & Automation',
    'Key Provider',
  ];
  readonly requiresKeyProvider = true;
  readonly requiresIdentityBackend = false;

  constructor(
    context: CryptoServiceContext,
    private readonly repositories: CryptoRepositories,
    private readonly keyProviderRegistry: KeyProviderRegistry
  ) {
    super(context);
  }

  async createSignature(request: SignatureCreationRequest): Promise<SignatureCreationResult> {
    const provider = this.keyProviderRegistry.getActiveProvider();
    const keyPair = this.repositories.keyPairs.findById(request.keyPairId);

    if (!keyPair || !this.keyProviderRegistry.isConfigured() || !provider.isReady()) {
      return {
        success: false,
        signatureId: null,
        signature: null,
        error: 'Key Provider not configured or not connected. No signatures created.',
        isPlaceholder: true,
        source: 'Key Provider — Not Configured',
      };
    }

    const signed = await provider.signMessage(
      request.keyPairId,
      request.messageHash,
      request.algorithm
    );

    if (!signed?.signature || !signed.signatureId) {
      return {
        success: false,
        signatureId: null,
        signature: null,
        error: 'Key Provider did not create a signature.',
        isPlaceholder: true,
        source: 'Key Provider — Operation Unavailable',
      };
    }

    const record: RTWOSignature = {
      signatureId: signed.signatureId,
      messageHash: request.messageHash,
      publicKey: keyPair.publicKey,
      signature: signed.signature,
      algorithm: request.algorithm,
      status: 'created',
      createdAt: Date.now(),
    };

    this.repositories.signatures.save(record);
    this.context.eventBus.emit(CryptoEventTypes.SIGNATURE_CREATED, signed.signatureId, {
      purpose: request.purpose,
    });

    return {
      success: true,
      signatureId: signed.signatureId,
      signature: signed.signature,
      error: null,
      source: 'Key Provider — Signature Created',
    };
  }

  getSignature(signatureId: SignatureId): RTWOSignature | null {
    if (!this.keyProviderRegistry.getActiveProvider().isReady()) {
      return null;
    }

    return this.repositories.signatures.findById(signatureId);
  }

  getSignaturesByStatus(status: SignatureStatus): RTWOSignature[] {
    if (!this.keyProviderRegistry.getActiveProvider().isReady()) {
      return [];
    }

    return this.repositories.signatures.findByStatus(status);
  }
}
